import { URLS } from "@/lib/consts";
import type { ServiceCategory, ServiceItem } from "../types";

const ACCOUNTING = URLS.SERVICES.ACCOUNTING;
const ADVISORY = URLS.SERVICES.ADVISORY;
const AUDIT = URLS.SERVICES.AUDIT;
const BUSINESS = URLS.SERVICES.BUSINESS;
const INDIVIDUAL = URLS.SERVICES.INDIVIDUAL;
const TAX = URLS.SERVICES.TAX;

/*
 * Accounting Items
 */
const ACCOUNTING_ITEMS: ServiceItem[] = [
  {
    key: "accounting-system-selection-and-setup",
    categoryKey: "accounting",
    label: "Accounting System Selection & Setup",
    description:
      "Set your business up for success with the right accounting infrastructure. We help you choose and configure systems tailored to your operations, industry, and reporting needs-ensuring clean books from day one.",
    tags: ["accounting", "advisory", "business"],
    href: ACCOUNTING.ACCOUNTING_SYSTEM_SELECTION_AND_SETUP,
    imageUrl: "accounting-system-selection-and-setup",
    imageAlt:
      "Accounting System Selection & Setup service in Houston, Texas - consulting, accounting",
    relatedKeys: [
      "internal-control-documentation",
      "policies-and-procedures",
      "part-time-controller-cfo",
      "budgeting-and-forecasts",
      "gaap-implementation",
    ],
  },
  {
    key: "compilation-of-financial-statements",
    categoryKey: "accounting",
    label: "Compilation of Financial Statements",
    description:
      "We compile clean, organized financial statements that give a clear picture of your business or organization's financial position—perfect for internal use, lenders, or strategic planning.",
    tags: ["accounting", "business"],
    href: ACCOUNTING.COMPILATION_OF_FINANCIAL_STATEMENTS,
    imageUrl: "compilation-of-financial-statements",
    imageAlt:
      "Compilation of Financial Statements service in Houston, Texas - financial reporting",
    relatedKeys: [
      "preparation-of-financial-statements",
      "review-of-financial-statements",
      "special-purpose-financial-statements",
      "audits-reviews-and-financial-statements",
      "gaap-implementation",
    ],
  },
  {
    key: "financial-position-job-descriptions",
    categoryKey: "accounting",
    label: "Financial Position Job Descriptions",
    description:
      "Build a better finance team with structured, CPA-crafted job descriptions. From controllers to bookkeepers, we define roles that align with your financial strategy and internal controls.",
    tags: ["accounting", "advisory", "business"],
    href: ACCOUNTING.FINANCIAL_POSITION_JOB_DESCRIPTIONS,
    imageUrl: "financial-position-job-descriptions",
    imageAlt:
      "Financial Position Job Descriptions service in Houston, Texas - consulting, accounting",
    relatedKeys: [
      "internal-control-documentation",
      "internal-controls",
      "policies-and-procedures",
      "part-time-controller-cfo",
      "development-of-financial-policies-and-procedures",
    ],
  },
  {
    key: "policies-and-procedures",
    categoryKey: "accounting",
    label: "Policies & Procedures",
    description:
      "Ensure consistency and compliance across your organization with professionally drafted financial policies and procedures. We tailor each document to your industry, size, and internal control goals.",
    tags: ["accounting", "advisory", "business", "audit"],
    href: ACCOUNTING.POLICIES_AND_PROCEDURES,
    imageUrl: "policies-and-procedures",
    imageAlt:
      "Policies & Procedures service in Houston, Texas - accounting, consulting",
    relatedKeys: [
      "internal-control-documentation",
      "internal-controls",
      "development-of-financial-policies-and-procedures",
      "part-time-controller-cfo",
      "accounting-system-selection-and-setup",
    ],
  },
  {
    key: "preparation-of-financial-statements",
    categoryKey: "accounting",
    label: "Preparation of Financial Statements",
    description:
      "Whether you need financials for internal use, investors, or lenders, we provide clear and accurate financial statement preparation for businesses and individuals across Texas.",
    tags: ["accounting", "business", "individual"],
    href: ACCOUNTING.PREPARATION_OF_FINANCIAL_STATEMENTS,
    imageUrl: "preparation-of-financial-statements",
    imageAlt:
      "Preparation of Financial Statements service in Houston, Texas — accounting, financial reporting",
    relatedKeys: [
      "compilation-of-financial-statements",
      "review-of-financial-statements",
      "special-purpose-financial-statements",
      "audits-reviews-and-financial-statements",
      "gaap-implementation",
    ],
  },
  {
    key: "software-selection-and-implementation",
    categoryKey: "accounting",
    label: "Software Selection & Implementation",
    description:
      "Choosing the right accounting software can transform your financial operations. We assess your needs and guide you through setup, training, and ongoing support for systems like QuickBooks and beyond.",
    tags: ["accounting", "advisory", "business"],
    href: ACCOUNTING.SOFTWARE_SELECTION_AND_IMPLEMENTATION,
    imageUrl: "software-selection-and-implementation",
    imageAlt:
      "Software Selection & Implementation service in Houston, Texas — accounting, business",
    relatedKeys: [
      "accounting-system-selection-and-setup",
      "part-time-controller-cfo",
      "cash-flow-management",
      "budgeting-and-forecasts",
      "policies-and-procedures",
    ],
  },
  {
    key: "quickbooks-services",
    categoryKey: "accounting",
    label: "QuickBooks Services",
    description:
      "Get the most out of QuickBooks with expert setup, training, and support. We help Houston businesses configure QuickBooks Online or Desktop, clean up existing files, and train your team for accurate, reliable books.",
    tags: ["accounting", "business"],
    href: ACCOUNTING.QUICKBOOKS_SERVICES,
    imageUrl: "software-selection-and-implementation",
    imageAlt:
      "QuickBooks setup, training, and support services in Houston, Texas",
    relatedKeys: [
      "accounting-system-selection-and-setup",
      "software-selection-and-implementation",
      "compilation-of-financial-statements",
      "part-time-controller-cfo",
      "cash-flow-management",
    ],
  },
];

/*
 * Advisory Items
 */
const ADVISORY_ITEMS: ServiceItem[] = [
  {
    key: "budgeting-and-forecasts",
    categoryKey: "advisory",
    label: "Budgeting & Forecasts",
    description:
      "Our team helps you build financial forecasts and budgets that support smart decisions. Whether you're growing your business or managing investments, we provide data-backed clarity for your future.",
    tags: ["advisory", "accounting", "business", "individual"],
    href: ADVISORY.BUDGETING_AND_FORECASTS,
    imageUrl: "budgeting-and-forecasts",
    imageAlt:
      "Budgeting & Forecasts service in Houston, Texas - advisory, consulting, business, accounting",
    relatedKeys: [
      "cash-flow-management",
      "part-time-controller-cfo",
      "policies-and-procedures",
      "gaap-implementation",
    ],
  },
  {
    key: "cash-flow-management",
    categoryKey: "advisory",
    label: "Cash Flow Management",
    description:
      "Maximize your financial health with our strategic cash flow management services. Orsak CPA helps you anticipate shortfalls, optimize revenue timing, and make informed spending decisions.",
    tags: ["advisory", "business", "individual", "accounting"],
    href: ADVISORY.CASH_FLOW_MANAGEMENT,
    imageUrl: "cash-flow-management",
    imageAlt:
      "Cash Flow Management service in Houston, Texas — advisory, business, individual, consulting, accounting",
    relatedKeys: [
      "budgeting-and-forecasts",
      "accounting-system-selection-and-setup",
      "part-time-controller-cfo",
      "policies-and-procedures",
      "gaap-implementation",
    ],
  },
  {
    key: "disclosures-and-budget-support",
    categoryKey: "advisory",
    label: "Disclosures & Budget Support",
    description:
      "Let us handle your financial disclosures and budgeting documentation with precision. Our Houston-based CPA firm ensures compliance and clarity in everything from footnotes to board presentations.",
    tags: ["advisory", "accounting", "audit", "business"],
    href: ADVISORY.DISCLOSURES_AND_BUDGET_SUPPORT,
    imageUrl: "disclosures-and-budget-support",
    imageAlt:
      "Disclosures & Budget Support service in Houston, Texas - advisory, consulting, accounting",
    relatedKeys: [
      "budgeting-and-forecasts",
      "gaap-implementation",
      "supplemental-schedules",
      "compilation-of-financial-statements",
    ],
  },
  {
    key: "gaap-implementation",
    categoryKey: "advisory",
    label: "GAAP Implementation",
    description:
      "Navigate complex GAAP standards with ease. We analyze how new pronouncements apply to your business, prepare supporting documentation, and draft financial disclosures that align with U.S. accounting principles.",
    tags: ["advisory", "accounting", "audit", "business"],
    href: ADVISORY.GAAP_IMPLEMENTATION,
    imageUrl: "gaap-implementation",
    imageAlt:
      "GAAP Implementation service in Houston, Texas - advisory, consulting, accounting",
    relatedKeys: [
      "budgeting-and-forecasts",
      "disclosures-and-budget-support",
      "supplemental-schedules",
      "audits-reviews-and-financial-statements",
      "special-purpose-financial-statements",
    ],
  },
  {
    key: "medicare-cost-reports",
    categoryKey: "advisory",
    label: "Medicare Cost Reports",
    description:
      "We specialize in preparing accurate Medicare cost reports for hospices and healthcare providers. Our compliance-first approach ensures your facility meets all CMS requirements and deadlines.",
    tags: ["advisory", "business", "accounting"],
    href: ADVISORY.MEDICARE_COST_REPORTS,
    imageUrl: "medicare-cost-reports",
    imageAlt:
      "Medicare Cost Reports service in Houston, Texas - advisory, healthcare",
    relatedKeys: [
      "supplemental-schedules",
      "gaap-implementation",
      "disclosures-and-budget-support",
    ],
  },
  {
    key: "succession-planning-and-wealth-transfer",
    categoryKey: "advisory",
    label: "Succession Planning & Wealth Transfer",
    description:
      "Preserve generational wealth with tax-smart succession and estate planning. We serve high-net-worth individuals and families with tailored strategies for asset transfer, trusts, and long-term legacy protection.",
    tags: ["advisory", "tax", "individual", "business"],
    href: ADVISORY.SUCCESSION_PLANNING_AND_WEALTH_TRANSFER,
    imageUrl: "succession-planning-and-wealth-transfer",
    imageAlt:
      "Succession Planning & Wealth Transfer service in Houston, Texas - advisory, individual, high net-worth",
    relatedKeys: ["tax-planning", "estate-and-trusts", "successful-planning"],
  },
  {
    key: "supplemental-schedules",
    categoryKey: "advisory",
    label: "Supplemental Schedules",
    description:
      "We create detailed supplemental schedules for financial closings, reconciliations, and audits. Our documentation supports accurate reporting and strengthens your internal accounting processes.",
    tags: ["advisory", "accounting", "audit", "business"],
    href: ADVISORY.SUPPLEMENTAL_SCHEDULES,
    imageUrl: "supplemental-schedules",
    imageAlt:
      "Supplemental Schedules service in Houston, Texas - advisory, consulting, accounting",
    relatedKeys: [
      "gaap-implementation",
      "disclosures-and-budget-support",
      "internal-control-documentation",
      "special-purpose-financial-statements",
    ],
  },
];

/*
 * Audit Items
 */
const AUDIT_ITEMS: ServiceItem[] = [
  {
    key: "audit-of-financial-statements",
    categoryKey: "audit",
    label: "Audit of Financial Statements",
    description:
      "Ensure full financial transparency and regulatory confidence with our audit services for non-public companies and not-for-profits in Houston, Texas. Orsak CPAs detailed audits help you meet stakeholder expectations and prepare for growth.",
    tags: ["audit", "accounting", "business"],
    href: AUDIT.AUDIT_OF_FINANCIAL_STATEMENTS,
    imageUrl: "audit-of-financial-statements",
    imageAlt:
      "Audit of Financial Statements service in Houston, Texas - audit, financial reporting",
    relatedKeys: [
      "review-of-financial-statements",
      "compilation-of-financial-statements",
      "special-purpose-financial-statements",
      "internal-control-documentation",
      "gaap-implementation",
    ],
  },
  {
    key: "audits-reviews-and-financial-statements",
    categoryKey: "audit",
    label: "Audits, Reviews, & Financial Statements",
    description:
      "From comprehensive audits to limited-scope reviews and compilations, we offer tailored financial reporting services that meet your compliance and operational needs. Trusted by Texas businesses and individuals alike.",
    tags: ["audit", "accounting", "business"],
    href: AUDIT.AUDITS_REVIEWS_AND_FINANCIAL_STATEMENTS,
    imageUrl: "audits-reviews-and-financial-statements",
    imageAlt:
      "Audits, Reviews, & Financial Statements service in Houston, Texas - audit, reviews, financial reporting",
    relatedKeys: [
      "audit-of-financial-statements",
      "review-of-financial-statements",
      "special-purpose-financial-statements",
      "compilation-of-financial-statements",
      "gaap-implementation",
    ],
  },
  {
    key: "internal-control-documentation",
    categoryKey: "audit",
    label: "Internal Control Documentation",
    description:
      "Protect your organization with properly designed and documented internal controls. We create customized control systems that strengthen oversight, reduce fraud risk, and ensure compliance.",
    tags: ["audit", "accounting", "advisory", "business"],
    href: AUDIT.INTERNAL_CONTROL_DOCUMENTATION,
    imageUrl: "internal-control-documentation",
    imageAlt:
      "Internal Control Documentation service in Houston, Texas - audit, accounting",
    relatedKeys: [
      "internal-controls",
      "policies-and-procedures",
      "part-time-controller-cfo",
      "supplemental-schedules",
    ],
  },
  {
    key: "internal-controls",
    categoryKey: "audit",
    label: "Internal Controls",
    description:
      "Our CPA team helps businesses design, assess, and improve internal controls that safeguard assets and improve operational efficiency-especially useful before audits, reviews, or funding rounds.",
    tags: ["audit", "advisory", "accounting", "business"],
    href: AUDIT.INTERNAL_CONTROLS,
    imageUrl: "internal-controls",
    imageAlt:
      "Internal Controls service in Houston, Texas - audit, advisory, accounting",
    relatedKeys: [
      "internal-control-documentation",
      "policies-and-procedures",
      "development-of-financial-policies-and-procedures",
      "part-time-controller-cfo",
    ],
  },
  {
    key: "review-of-financial-statements",
    categoryKey: "audit",
    label: "Review of Financial Statements",
    description:
      "Get limited assurance with a formal financial statement review-ideal for lenders or stakeholders who need confidence without a full audit. We serve non-public businesses and not-for-profits across Texas.",
    tags: ["audit", "accounting", "business"],
    href: AUDIT.REVIEW_OF_FINANCIAL_STATEMENTS,
    imageUrl: "review-of-financial-statements",
    imageAlt:
      "Review of Financial Statements service in Houston, Texas - reviews, audit, financial reporting",
    relatedKeys: [
      "audit-of-financial-statements",
      "audits-reviews-and-financial-statements",
      "compilation-of-financial-statements",
      "preparation-of-financial-statements",
    ],
  },
  {
    key: "special-purpose-financial-statements",
    categoryKey: "audit",
    label: "Special Purpose Financial Statements",
    description:
      "Whether you need a projection, forecast, or pro-forma report, we prepare special purpose financials that meet your exact reporting goals and stakeholder requirements.",
    tags: ["audit", "accounting", "advisory", "business"],
    href: AUDIT.SPECIAL_PURPOSE_FINANCIAL_STATEMENTS,
    imageUrl: "special-purpose-financial-statements",
    imageAlt:
      "Special Purpose Financial Statements service in Houston, Texas - audit, financial reporting",
    relatedKeys: [
      "audits-reviews-and-financial-statements",
      "review-of-financial-statements",
      "budgeting-and-forecasts",
      "gaap-implementation",
    ],
  },
];

/*
 * Business Items
 */
const BUSINESS_ITEMS: ServiceItem[] = [
  {
    key: "development-of-financial-policies-and-procedures",
    categoryKey: "business",
    label: "Development of Financial Policies and Procedures",
    description:
      "Establish solid financial foundations with well-documented policies and procedures. Our firm helps Texas businesses define internal controls, ensure consistency, and support compliance at every level.",
    tags: ["business", "accounting", "advisory", "audit"],
    href: BUSINESS.DEVELOPMENT_OF_FINANCIAL_POLICIES_AND_PROCEDURES,
    imageUrl: "development-of-financial-policies-and-procedures",
    imageAlt:
      "Development of Financial Policies and Procedures service in Houston, Texas - consulting, accounting",
    relatedKeys: [
      "policies-and-procedures",
      "internal-control-documentation",
      "internal-controls",
      "part-time-controller-cfo",
    ],
  },
  {
    key: "debt-covenant-compliance",
    categoryKey: "business",
    label: "Debt Covenant Compliance",
    description:
      "Avoid penalties and maintain good standing with lenders through proactive debt covenant monitoring. We help Houston businesses meet their financial obligations and stay audit-ready.",
    tags: ["business", "advisory", "accounting"],
    href: BUSINESS.DEBT_COVENANT_COMPLIANCE,
    imageUrl: "debt-covenant-compliance",
    imageAlt:
      "Debt Covenant Compliance service in Houston, Texas - advisory, consulting, business, accounting",
    relatedKeys: [
      "part-time-controller-cfo",
      "cash-flow-management",
      "internal-control-documentation",
      "budgeting-and-forecasts",
    ],
  },
  {
    key: "development-of-budgets",
    categoryKey: "business",
    label: "Development of Budgets",
    description:
      "We work closely with clients to build strategic, goal-oriented budgets. Whether you're expanding operations or streamlining expenses, our CPA-guided budget development brings financial clarity and control.",
    tags: ["business", "advisory", "accounting"],
    href: BUSINESS.DEVELOPMENT_OF_BUDGETS,
    imageUrl: "development-of-budgets",
    imageAlt:
      "Development of Budgets service in Houston, Texas - advisory, consulting, business, accounting",
    relatedKeys: [
      "budgeting-and-forecasts",
      "cash-flow-management",
      "part-time-controller-cfo",
      "policies-and-procedures",
    ],
  },
  {
    key: "successful-planning",
    categoryKey: "business",
    label: "Successful Planning",
    description:
      "From retirement to business succession, our strategic planning services help you clarify goals, reduce tax impact, and make smart financial moves-especially for high-net-worth individuals and family-owned companies.",
    tags: ["business", "advisory", "individual", "tax"],
    href: BUSINESS.SUCCESSFUL_PLANNING,
    imageUrl: "successful-planning",
    imageAlt:
      "Successful Planning service in Houston, Texas - advisory, consulting, individual, business",
    relatedKeys: [
      "succession-planning-and-wealth-transfer",
      "tax-planning",
      "estate-and-trusts",
    ],
  },
  {
    key: "part-time-controller-cfo",
    categoryKey: "business",
    label: "Part-Time Controller/CFO",
    description:
      "Gain executive-level financial insight without a full-time hire. Our part-time Controller and CFO services help Texas businesses manage growth, oversee reporting, and make strategic financial decisions.",
    tags: ["business", "advisory", "accounting"],
    href: BUSINESS.PART_TIME_CONTROLLER_CFO,
    imageUrl: "part-time-controller-cfo",
    imageAlt:
      "Part-Time Controller/CFO service in Houston, Texas - advisory, consulting, business, accounting",
    relatedKeys: [
      "accounting-system-selection-and-setup",
      "cash-flow-management",
      "budgeting-and-forecasts",
      "internal-control-documentation",
      "development-of-financial-policies-and-procedures",
    ],
  },
];

/*
 * Individual Items
 */
const INDIVIDUAL_ITEMS: ServiceItem[] = [
  {
    key: "individual-accounting",
    categoryKey: "individual",
    label: "Individual Accounting",
    description:
      "Get personalized accounting services tailored to high-income individuals, families, and professionals. We manage everything from asset tracking to tax reporting with discretion and precision.",
    tags: ["individual", "accounting", "tax"],
    href: INDIVIDUAL.INDIVIDUAL_ACCOUNTING,
    imageUrl: "individual-accounting",
    imageAlt:
      "Individual Accounting service in Houston, Texas - individual, accounting",
    relatedKeys: [
      "personal-financial-statements",
      "tax-planning",
      "federal-tax-return-preparation",
      "tax-compliance",
    ],
  },
  {
    key: "personal-financial-statements",
    categoryKey: "individual",
    label: "Personal Financial Statements",
    description:
      "We prepare detailed personal financial statements for high net-worth individuals, entrepreneurs, and family offices. Ideal for wealth management, estate planning, and financial disclosures.",
    tags: ["individual", "accounting", "advisory", "tax"],
    href: INDIVIDUAL.PERSONAL_FINANCIAL_STATEMENTS,
    imageUrl: "personal-financial-statements",
    imageAlt:
      "Personal Financial Statements service in Houston, Texas - individual, financial reporting, high net-worth",
    relatedKeys: [
      "individual-accounting",
      "succession-planning-and-wealth-transfer",
      "estate-and-trusts",
      "tax-planning",
    ],
  },
];

/*
 * Tax Items
 */
const TAX_ITEMS: ServiceItem[] = [
  {
    key: "estate-and-trusts",
    categoryKey: "tax",
    label: "Estate & Trusts",
    description:
      "Secure your legacy with tailored estate and trust planning. We assist individuals, families, and business owners across Texas with tax-efficient strategies for wealth transfer and long-term protection.",
    tags: ["tax", "advisory", "individual", "business"],
    href: TAX.ESTATE_AND_TRUSTS,
    imageUrl: "estate-and-trusts",
    imageAlt:
      "Estate & Trusts service in Houston, Texas - tax, advisory, individual, high net-worth",
    relatedKeys: [
      "succession-planning-and-wealth-transfer",
      "successful-planning",
      "tax-planning",
    ],
  },
  {
    key: "federal-tax-return-preparation",
    categoryKey: "tax",
    label: "Federal Tax Return Preparation",
    description:
      "Ensure IRS compliance with accurate, timely federal tax filings. We serve individuals and businesses in Houston, delivering tax preparation services that minimize risk and maximize savings.",
    tags: ["tax", "individual", "business"],
    href: TAX.FEDERAL_TAX_RETURN_PREPARATION,
    imageUrl: "federal-tax-return-preparation",
    imageAlt:
      "Federal Tax Return Preparation service in Houston, Texas - tax, individual, business",
    relatedKeys: [
      "tax-compliance",
      "tax-planning",
      "state-franchise-income-and-tax-return",
      "individual-accounting",
    ],
  },
  {
    key: "tax-planning",
    categoryKey: "tax",
    label: "Tax Planning",
    description:
      "Minimize your tax burden with forward-thinking strategies tailored for both individuals and businesses. Our Houston-based CPAs advise high-net-worth clients and owners on timing, deductions, and entity structure.",
    tags: ["tax", "advisory", "individual", "business"],
    href: TAX.TAX_PLANNING,
    imageUrl: "tax-planning",
    imageAlt:
      "Tax Planning service in Houston, Texas - tax, advisory, individual, business",
    relatedKeys: [
      "estate-and-trusts",
      "succession-planning-and-wealth-transfer",
      "successful-planning",
    ],
  },
  {
    key: "state-franchise-income-and-tax-return",
    categoryKey: "tax",
    label: "State Franchise Income & Tax Return",
    description:
      "Stay compliant with Texas franchise tax laws and other state-specific filing requirements. We manage state income and franchise tax returns accurately and on time for both businesses and individuals.",
    tags: ["tax", "business", "advisory"],
    href: TAX.STATE_FRANCHISE_INCOME_AND_TAX_RETURN,
    imageUrl: "state-franchise-income-and-tax-return",
    imageAlt:
      "State Franchise Income & Tax Return service in Houston, Texas - tax, business",
    relatedKeys: [
      "federal-tax-return-preparation",
      "tax-compliance",
      "tax-planning",
    ],
  },
  {
    key: "tax-compliance",
    categoryKey: "tax",
    label: "Tax Compliance",
    description:
      "Stay fully compliant with ever-changing tax laws. We assist individuals and businesses in meeting all local, state, and federal filing requirements-avoiding penalties and ensuring peace of mind.",
    tags: ["tax", "business", "individual"],
    href: TAX.TAX_COMPLIANCE,
    imageUrl: "tax-compliance",
    imageAlt: "Tax Compliance service in Houston, Texas - tax, individual",
    relatedKeys: [
      "federal-tax-return-preparation",
      "state-franchise-income-and-tax-return",
      "tax-planning",
    ],
  },
  {
    key: "irs-resolution",
    categoryKey: "tax",
    label: "IRS Tax Resolution",
    description:
      "Behind on taxes or facing IRS action? We help Houston taxpayers resolve back taxes, liens, levies, garnishment, and unfiled returns—and represent you before the IRS.",
    tags: ["tax", "individual", "business"],
    href: TAX.IRS_RESOLUTION,
    imageUrl: "tax",
    imageAlt:
      "IRS tax resolution and representation service in Houston, Texas - tax, individual",
    relatedKeys: [
      "tax-planning",
      "federal-tax-return-preparation",
      "tax-compliance",
      "estate-and-trusts",
    ],
  },
  {
    key: "oil-and-gas-royalty-tax",
    categoryKey: "tax",
    label: "Oil & Gas Royalty Owner Tax",
    description:
      "Royalty and mineral owners face tax rules most preparers rarely see. We handle depletion, intangible drilling costs, working vs. royalty interest, and the K-1s and 1099s behind your division orders.",
    tags: ["tax", "individual", "business", "advisory"],
    href: TAX.OIL_AND_GAS_ROYALTY_TAX,
    imageUrl: "oil-and-gas",
    imageAlt:
      "Oil & gas royalty owner tax service in Houston, Texas - depletion, IDCs, mineral interests",
    relatedKeys: [
      "ranch-and-landowner-tax",
      "real-estate-investor-tax",
      "estate-and-trusts",
      "succession-planning-and-wealth-transfer",
      "tax-planning",
    ],
  },
  {
    key: "ranch-and-landowner-tax",
    categoryKey: "tax",
    label: "Ranch, Farm & Landowner Tax",
    description:
      "Texas land carries its own tax playbook—ag and wildlife valuation, rollback exposure, mineral and royalty income, conservation easements, and land 1031s. We help owners keep more across generations.",
    tags: ["tax", "individual", "business", "advisory"],
    href: TAX.RANCH_AND_LANDOWNER_TAX,
    imageUrl: "ranching-and-agribusiness",
    imageAlt:
      "Ranch, farm, and landowner tax service in Houston and Texas - ag valuation, mineral income, land 1031",
    relatedKeys: [
      "oil-and-gas-royalty-tax",
      "real-estate-investor-tax",
      "estate-and-trusts",
      "succession-planning-and-wealth-transfer",
      "tax-planning",
    ],
  },
  {
    key: "real-estate-investor-tax",
    categoryKey: "tax",
    label: "Real Estate Investor Tax",
    description:
      "Rentals, syndications, and flips reward investors who plan. We coordinate 1031 exchanges, cost segregation and bonus depreciation, real estate professional status, and passive-activity rules to cut your tax bill.",
    tags: ["tax", "individual", "business", "advisory"],
    href: TAX.REAL_ESTATE_INVESTOR_TAX,
    imageUrl: "real-estate",
    imageAlt:
      "Real estate investor tax service in Houston, Texas - 1031 exchanges, cost segregation, REP status",
    relatedKeys: [
      "oil-and-gas-royalty-tax",
      "ranch-and-landowner-tax",
      "business-sale-exit",
      "tax-planning",
      "estate-and-trusts",
    ],
  },
  {
    key: "business-sale-exit",
    categoryKey: "tax",
    label: "Business Sale & Exit Planning",
    description:
      "How you structure a sale can swing your after-tax proceeds by millions. We plan QSBS, installment sales, asset vs. stock structuring, and purchase-price allocation—well before the LOI.",
    tags: ["tax", "business", "advisory", "individual"],
    href: TAX.BUSINESS_SALE_EXIT,
    imageUrl: "successful-planning",
    imageAlt:
      "Business sale and exit planning service in Houston, Texas - QSBS, installment sales, deal structuring",
    relatedKeys: [
      "succession-planning-and-wealth-transfer",
      "successful-planning",
      "estate-and-trusts",
      "tax-planning",
      "real-estate-investor-tax",
    ],
  },
];

/*
 * Service Categories
 */
export const SERVICE_CATEGORIES: ServiceCategory[] = [
  {
    key: "accounting",
    tags: ["accounting"],
    label: "Accounting & Controller Services",
    description:
      "Reliable audit and assurance services that deliver clarity and trust. We help businesses meet compliance standards and gain valuable financial insights.",
    href: ACCOUNTING.ROOT,
    imageUrl: "accounting",
    imageAlt:
      "Orsak CPA’s in Houston provides comprehensive accounting services for businesses, nonprofits, and individuals. From financial statements to cash flow management, our CPAs deliver clarity and compliance.",
    items: ACCOUNTING_ITEMS,
  },
  {
    key: "advisory",
    tags: ["advisory"],
    label: "Consulting & Advisory Services",
    description:
      "Personalized wealth management strategies to help you grow, protect, and preserve your assets. Plan confidently for retirement, investment, and beyond.",
    href: ADVISORY.ROOT,
    imageUrl: "advisory",
    imageAlt:
      "Orsak CPA’s partners with businesses and individuals in Houston, Texas to provide strategic advisory services. From budgeting to succession planning, our CPAs guide financial success.",
    items: ADVISORY_ITEMS,
  },
  {
    key: "audit",
    tags: ["audit"],
    label: "Audit, Reviews, & Financial Reports Services",
    description:
      "Expert support for business growth and financial success. We provide strategic accounting, advisory, and consulting services to help your business thrive.",
    href: AUDIT.ROOT,
    imageUrl: "audit",
    imageAlt:
      "Gain confidence in your financial reporting with Orsak CPA’s. Based in Houston, Texas, our CPAs provide audits, reviews, and assurance services tailored to your organization’s needs.",
    items: AUDIT_ITEMS,
  },
  {
    key: "business",
    tags: ["business"],
    label: "Business Services",
    description:
      "Streamlined, accurate payroll processing for businesses of all sizes. Ensure compliance and simplify your payroll operations with expert solutions.",
    href: BUSINESS.ROOT,
    imageUrl: "business",
    imageAlt:
      "Orsak CPA’s supports Houston businesses with specialized accounting, audit, tax, and advisory services. Our CPAs provide financial strategies to help companies grow and succeed.",
    items: BUSINESS_ITEMS,
  },
  {
    key: "individual",
    tags: ["individual"],
    label: "Individual Services",
    description:
      "Full-service accounting to keep your finances organized and optimized. From bookkeeping to financial reporting, we support your business at every stage.",
    href: INDIVIDUAL.ROOT,
    imageUrl: "individual",
    imageAlt:
      "Orsak CPA’s in Houston provides personalized accounting and tax services for individuals and families. Our CPAs offer guidance in tax returns, planning, and financial management.",
    items: INDIVIDUAL_ITEMS,
  },
  {
    key: "tax",
    tags: ["tax"],
    label: "Tax Services",
    description:
      "Comprehensive tax planning and preparation for individuals and businesses. Maximize savings, stay compliant, and reduce tax liabilities with expert guidance.",
    href: TAX.ROOT,
    imageUrl: "tax",
    imageAlt:
      "Orsak CPA’s in Houston delivers expert tax services for businesses and individuals. Our CPAs provide compliance, preparation, and proactive planning strategies to minimize liabilities.",
    items: TAX_ITEMS,
  },
];

export const SERVICE_CATEGORIES_BY_KEY = Object.fromEntries(
  SERVICE_CATEGORIES.map(c => [c.key, c]),
);

export const ALL_SERVICE_ITEMS: ServiceItem[] = SERVICE_CATEGORIES.flatMap(
  category => category.items,
);

export const SERVICE_ITEMS_BY_KEY = Object.fromEntries(
  ALL_SERVICE_ITEMS.map(category => [category.key, category]),
);
